import React from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { toggleChat } from 'apps/auth/actions/chatAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DocsList } from 'apps/auth/components/common/components/DocsList/DocsList';
import { DocsListItem } from 'apps/auth/components/common/components/DocsList/DocsListItem';
import { DocsBitText, DocsIllustration, DocsTable, DocsTd, DocsTr } from 'apps/auth/components/common/components/DocsList/styles';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { Text } from 'apps/auth/components/common/Text/Text';
import { AML_LEVEL_FORM } from 'apps/auth/constants/formName';
import { URL_IMAGES_I } from 'apps/auth/constants/UrlConstants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { isSpecialCountry, resolveArguments } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

import ProfileAmlSsnSelect from './ProfileAmlSsnSelect';

const ProfileAmlLevelDocs = (props) => {
    const location = useLocation();
    const {
        docs_limits_title,
        income_limits_title,
        docsOr,
        amlLimitUsaDocs1,
        amlLimitUsaDocs2,
        amlLimitUsaDocs3,
        amlLimitUsaDocs4,
        amlLimitDocs1,
        amlLimitDocs2,
        amlLimitDocs3,
        amlLimitDocs4,
        amlLimitsUsaResidence1,
        amlLimitsUsaResidence2,
        amlLimitsUsaResidence3,
        amlLimitsUsaResidence4,
        amlLimitsResidence1,
        amlLimitsResidence2,
        amlLimitsResidence3,
        amlLimitsResidence4,
        residence_limits_title,
        chat_limits_title,
        chat_limits_preview,
        wait_limits_title,
        wait_limits_preview,
        chatTitle,
        amlLimitIncomeBlock,
    } = usePageMessages();
    const { countryId: userCountry } = useAuth();
    const { current_limit, profile_form: profileForm } = useExtra();

    const isShowSsn = userCountry === USA && location?.pathname === '/auth/profile/aml/level/1/docs';

    if (isShowSsn) {
        return <ProfileAmlSsnSelect formName={AML_LEVEL_FORM} />;
    }

    return (
        <>
            <DocsList>
                <DocsListItem title={docs_limits_title}>
                    <DocsTable>
                        <DocsTr>
                            <DocsTd>
                                <DocsIllustration>
                                    <img src={`${URL_IMAGES_I}/kyc-passport.svg`} alt="passport" />
                                </DocsIllustration>
                                <DocsBitText>{docsOr}</DocsBitText>
                            </DocsTd>
                            <DocsTd>
                                <DocsIllustration>
                                    <img src={`${URL_IMAGES_I}/kyc-id-card.svg`} alt="idcard" />
                                </DocsIllustration>
                            </DocsTd>
                        </DocsTr>
                        {userCountry === USA && (
                            <DocsTr>
                                <DocsTd>
                                    <Text variant={'paragraph'} indent={'16px'}>
                                        {amlLimitUsaDocs1}
                                    </Text>
                                    <Text variant={'paragraph'}>{amlLimitUsaDocs2}</Text>
                                </DocsTd>
                                <DocsTd>
                                    <Text variant={'paragraph'} indent={'16px'}>
                                        {amlLimitUsaDocs3}
                                    </Text>
                                    <Text variant={'paragraph'}>{amlLimitUsaDocs4}</Text>
                                </DocsTd>
                            </DocsTr>
                        )}
                        {userCountry !== USA && (
                            <DocsTr>
                                <DocsTd>
                                    <Text variant={'paragraph'} indent={'16px'}>
                                        {amlLimitDocs1}
                                    </Text>
                                    <Text variant={'paragraph'}>{amlLimitDocs2}</Text>
                                </DocsTd>
                                <DocsTd>
                                    <Text variant={'paragraph'} indent={'16px'}>
                                        {amlLimitDocs3}
                                    </Text>
                                    <Text variant={'paragraph'}>{amlLimitDocs4}</Text>
                                </DocsTd>
                            </DocsTr>
                        )}
                    </DocsTable>
                </DocsListItem>
                {!isSpecialCountry(userCountry) && current_limit >= 2 && (
                    <DocsListItem title={residence_limits_title}>
                        <DocsTable>
                            <DocsTr>
                                <DocsTd>
                                    <DocsIllustration>
                                        <img src={`${URL_IMAGES_I}/kyc-bank-doc.svg`} alt="illustration" />
                                    </DocsIllustration>
                                    <DocsBitText>{docsOr}</DocsBitText>
                                </DocsTd>
                                <DocsTd>
                                    <DocsIllustration>
                                        <img src={`${URL_IMAGES_I}/kyc-payslips.svg`} alt="illustration" />
                                    </DocsIllustration>
                                </DocsTd>
                            </DocsTr>
                            {userCountry === USA && (
                                <DocsTr>
                                    <DocsTd>
                                        <Text variant={'paragraph'} indent={'16px'}>
                                            {amlLimitsUsaResidence1}
                                        </Text>
                                        <Text variant={'paragraph'}>{amlLimitsUsaResidence2}</Text>
                                    </DocsTd>
                                    <DocsTd>
                                        <Text variant={'paragraph'} indent={'16px'}>
                                            {amlLimitsUsaResidence3}
                                        </Text>
                                        <Text variant={'paragraph'}>{amlLimitsUsaResidence4}</Text>
                                    </DocsTd>
                                </DocsTr>
                            )}
                            {userCountry !== USA && (
                                <DocsTr>
                                    <DocsTd>
                                        <Text variant={'paragraph'} indent={'16px'}>
                                            {amlLimitsResidence1}
                                        </Text>
                                        <Text variant={'paragraph'}>{amlLimitsResidence2}</Text>
                                    </DocsTd>
                                    <DocsTd>
                                        <Text variant={'paragraph'} indent={'16px'}>
                                            {amlLimitsResidence3}
                                        </Text>
                                        <Text variant={'paragraph'}>{amlLimitsResidence4}</Text>
                                    </DocsTd>
                                </DocsTr>
                            )}
                        </DocsTable>
                    </DocsListItem>
                )}
                {(current_limit >= 3 || (isSpecialCountry(userCountry) && current_limit >= 2)) && (
                    <DocsListItem title={income_limits_title}>
                        <DocsTable>
                            <DocsTr>
                                <DocsTd>
                                    <DocsIllustration>
                                        <img src={`${URL_IMAGES_I}/kyc-bank-doc-plain.svg`} alt="illustration" />
                                    </DocsIllustration>
                                </DocsTd>
                            </DocsTr>
                            <DocsTr>
                                <DocsTd>
                                    <p>{amlLimitIncomeBlock}</p>
                                </DocsTd>
                            </DocsTr>
                        </DocsTable>
                    </DocsListItem>
                )}
                <DocsListItem title={chat_limits_title}>
                    <p>{resolveArguments(chat_limits_preview, profileForm?.id)}</p>
                </DocsListItem>
                <DocsListItem title={wait_limits_title}>
                    <p>{wait_limits_preview}</p>
                </DocsListItem>
            </DocsList>
            <FixedBottomPanel isRightAligned>
                <Button onClick={() => props.toggleChat(true)} raised={true} size="fit" caption={chatTitle} />
            </FixedBottomPanel>
        </>
    );
};
export default connect(null, { toggleChat })(ProfileAmlLevelDocs);
